import type { SceneNode } from '@aod/protocol';
import { useFrame, useThree } from '@react-three/fiber';
// The Action mode's in-Canvas rig (docs/aod-studio-design.md §4.1, phase A):
// the camera drops to eye height over the board, the player walks it with
// WASD through the hex walk model, the mouse looks (pointer lock). The
// strategy camera is disabled while active and its pose is restored on exit.
// The FP view presents; the sim decides (§4.2).
import { type RefObject, useEffect, useMemo, useReducer, useRef } from 'react';
import { GuardFigure } from './ai/GuardFigure.js';
import { type EnemyBand, spawnEnemyBand, updateEnemyBand } from './ai/enemies.js';
import { CharacterFigure } from './character/CharacterFigure.js';
import { type StepInput, spawnNear, walkStep, worldToAxial } from './hexwalk.js';
import { type LookState, applyLook, lookDirection } from './look.js';

const EYE_HEIGHT = 0.5;
const WALK_SPEED = 2.2;
const LOOK_SENSITIVITY = 0.0022;

interface Pose {
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  targetX: number;
  targetZ: number;
}

export function biomeLookup(
  tiles: readonly SceneNode[],
): (q: number, r: number) => string | undefined {
  const map = new Map<string, string>();
  for (const tile of tiles) {
    const p = tile.props as { q?: number; r?: number; biome?: string } | undefined;
    if (p?.q !== undefined && p.r !== undefined && p.biome) map.set(`${p.q},${p.r}`, p.biome);
  }
  return (q, r) => map.get(`${q},${r}`);
}

interface Props {
  readonly enabled: boolean;
  readonly controls: RefObject<{
    enabled: boolean;
    target: { x: number; z: number; set(x: number, y: number, z: number): void };
  } | null>;
  readonly tiles: readonly SceneNode[];
  readonly onExit: () => void;
}

export function ActionMode({ enabled, controls, tiles, onExit }: Props) {
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  const walk = useRef<{ x: number; z: number }>({ x: 0, z: 0 });
  const look = useRef<LookState>({ yaw: 0, pitch: -0.12 });
  const keys = useRef<Record<string, boolean>>({});
  const pressed = useRef<StepInput>({ forward: false, back: false, left: false, right: false });
  const savedPose = useRef<Pose | null>(null);
  const band = useRef<EnemyBand | null>(null);
  // The guards move outside React (a plain ref mutated in useFrame);
  // this tick forces the render that shows their new positions.
  const [, tickFrame] = useReducer((n: number) => n + 1, 0);
  const biomeAt = useMemo(() => biomeLookup(tiles), [tiles]);
  // The guards obey the player's passability rule; the passable hexes
  // feed the band's muster.
  const passable = useMemo(() => {
    const ok: { q: number; r: number }[] = [];
    for (const tile of tiles) {
      const p = tile.props as { q?: number; r?: number; biome?: string } | undefined;
      if (p?.q !== undefined && p.r !== undefined && p.biome !== undefined) {
        if (p.biome !== 'ocean' && p.biome !== 'mountain') ok.push({ q: p.q, r: p.r });
      }
    }
    return ok;
  }, [tiles]);
  const passableAt = useMemo(() => {
    const set = new Set(passable.map((t) => `${t.q},${t.r}`));
    return (q: number, r: number) => set.has(`${q},${r}`);
  }, [passable]);

  useEffect(() => {
    if (!enabled) return;
    // Enter: capture the strategy pose, drop to eye height over the orbit
    // target — at the nearest PASSABLE tile to it (a corner ocean tile once
    // had the camera standing in the sea, unable to walk anywhere) — and
    // park the orbit controls.
    const c = camera;
    savedPose.current = {
      x: c.position.x,
      y: c.position.y,
      z: c.position.z,
      qx: c.quaternion.x,
      qy: c.quaternion.y,
      qz: c.quaternion.z,
      qw: c.quaternion.w,
      targetX: controls.current?.target.x ?? 0,
      targetZ: controls.current?.target.z ?? 0,
    };
    if (controls.current) controls.current.enabled = false;
    const spawn = spawnNear(
      { x: controls.current?.target.x ?? 0, z: controls.current?.target.z ?? 0 },
      tiles,
    ) ?? { x: controls.current?.target.x ?? 0, z: controls.current?.target.z ?? 0 };
    walk.current = { x: spawn.x, z: spawn.z };
    look.current = { yaw: 0, pitch: -0.12 };
    // Muster the guard band around the spawn (phase D): deterministic
    // from the spawn hex, so the same drop point always meets the same
    // guards. Strikes are presented, not resolved — the combat wiring
    // consumes them later (§4.2: the FP view presents; the sim decides).
    const spawnHex = worldToAxial(spawn.x, spawn.z);
    band.current = spawnEnemyBand(`action-band:${spawnHex.q},${spawnHex.r}`, spawn, passable);

    const onMouseMove = (e: MouseEvent) => {
      look.current = applyLook(look.current, e.movementX, e.movementY, LOOK_SENSITIVITY);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      keys.current[e.code] = true;
      if (e.code === 'Escape') onExit();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      keys.current[e.code] = false;
    };
    const onPointerLockChange = () => {
      if (document.pointerLockElement !== gl.domElement) onExit();
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    gl.domElement.addEventListener('click', () => gl.domElement.requestPointerLock());

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      // Exit pointer lock best-effort; the browser no-ops if not locked.
      void document.exitPointerLock?.();
      // Restore the strategy camera: pose back, orbit controls back.
      const pose = savedPose.current;
      if (pose) {
        c.position.set(pose.x, pose.y, pose.z);
        c.quaternion.set(pose.qx, pose.qy, pose.qz, pose.qw);
      }
      if (controls.current) {
        controls.current.enabled = true;
        if (pose) controls.current.target.set(pose.targetX, 0, pose.targetZ);
      }
    };
  }, [enabled, camera, controls, gl, onExit, passable]);

  useFrame((_, delta) => {
    if (!enabled) return;
    pressed.current = {
      forward: keys.current.KeyW === true,
      back: keys.current.KeyS === true,
      left: keys.current.KeyA === true,
      right: keys.current.KeyD === true,
    };
    const stepped = walkStep(
      walk.current,
      look.current.yaw,
      pressed.current,
      WALK_SPEED,
      delta,
      biomeAt,
    );
    walk.current = { x: stepped.x, z: stepped.z };
    if (band.current) updateEnemyBand(band.current, walk.current, delta, passableAt);
    tickFrame();
    const dir = lookDirection(look.current);
    camera.position.set(walk.current.x, EYE_HEIGHT, walk.current.z);
    camera.lookAt(walk.current.x + dir[0], EYE_HEIGHT + dir[1], walk.current.z + dir[2]);
  });

  // The body: visible when the player looks down or back. The FP camera is
  // the figure's eyes; the figure stands at the walk position. The guard
  // band stands its posts around the spawn and acts on its own brain.
  const guards = band.current?.enemies ?? [];
  return (
    <>
      <CharacterFigure x={walk.current.x} z={walk.current.z} heading={look.current.yaw + Math.PI} />
      {guards.map((e) => (
        <GuardFigure
          key={e.id}
          x={e.x}
          z={e.z}
          heading={e.heading}
          seedKey={e.id}
          lunge={e.strikeRemaining > 0 ? 1 : 0}
        />
      ))}
    </>
  );
}
